'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import { Drawer } from '@ui/components/common/drawer';
import { Activity } from '@website/services/activities.service';
import type { SearchHub } from '@website/services/search.service';

import { ActivityDrawerContent } from './ActivityDrawerContent';
import { HubDrawerContent } from './HubDrawerContent';

interface ActivityDrawerProps {
  activity: Activity | null;
  /** A Hub's pin tapped: the drawer shows the Hub (in place of `activity`), its activities one arrow away. */
  hub?: { hub: SearchHub; activityCount: number } | null;
  isOpen: boolean;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  currentIndex?: number;
  totalCount?: number;
}

export function ActivityDrawer({
  activity,
  hub,
  isOpen,
  onClose,
  onPrev,
  onNext,
  currentIndex,
  totalCount
}: ActivityDrawerProps) {
  const t = useTranslations('ActivityDrawer');

  if (!isOpen || (!activity && !hub)) {
    return null;
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(openStatus: boolean) => {
        if (!openStatus) {
          onClose();
        }
      }}
      side="bottom"
      trigger={<span className="sr-only">{t('srOpen')}</span>}
      className="px-5 pt-0 pb-0 max-h-[85dvh] overflow-y-auto overscroll-contain rounded-t-xl"
      showHandle={true}
      title={t('title')}
    >
      {hub ? (
        <HubDrawerContent
          hub={hub.hub}
          activityCount={hub.activityCount}
          onPrev={onPrev}
          onNext={onNext}
          currentIndex={currentIndex}
          totalCount={totalCount}
        />
      ) : (
        <ActivityDrawerContent
          activity={activity}
          onPrev={onPrev}
          onNext={onNext}
          currentIndex={currentIndex}
          totalCount={totalCount}
        />
      )}
    </Drawer>
  );
}
